import { Button } from "../../controls/Button.js";
import { Chart, type ChartAt, type ChartSeries } from "../../controls/Chart.js";
import { ChartPanes } from "../../controls/ChartPanes.js";
import { ChartLink } from "../../controls/ChartLink.js";
import type { Ohlc } from "../../controls/chartScale.js";
import { CheckBox } from "../../controls/CheckBox.js";
import { ColorPicker } from "../../controls/ColorPicker.js";
import { ComboBox } from "../../controls/ComboBox.js";
import { DatePicker } from "../../controls/DatePicker.js";
import { ImageControl } from "../../controls/ImageControl.js";
import { Label } from "../../controls/Label.js";
import { LinkLabel } from "../../controls/LinkLabel.js";
import { ListBox } from "../../controls/ListBox.js";
import { ListView, type ListViewColumn } from "../../controls/ListView.js";
import { NumericUpDown } from "../../controls/NumericUpDown.js";
import { ProgressBar } from "../../controls/ProgressBar.js";
import { RadioButton } from "../../controls/RadioButton.js";
import { Separator } from "../../controls/Separator.js";
import { Slider } from "../../controls/Slider.js";
import { Spinner } from "../../controls/Spinner.js";
import { TextArea } from "../../controls/TextArea.js";
import { TextBox } from "../../controls/TextBox.js";
import { TreeView } from "../../controls/TreeView.js";
import type { TreeNodeSpec } from "../../controls/TreeNode.js";
import { registerElement, type Attrs, type BuildContext } from "../registry.js";
import type { XmlElement } from "../xml.js";

const ORIENTATIONS = ["horizontal", "vertical"] as const;
const TEXT_ALIGNS = ["left", "center", "right"] as const;
const SELECTION_MODES = ["single", "multiple", "none"] as const;
const CHART_KINDS = [
  "line",
  "area",
  "bar",
  "step",
  "points",
  "candlestick",
  "ohlc",
  "band",
  "range",
  "baseline",
  "waterfall",
  "renko",
  "kagi",
  "pointfigure",
] as const;
const CHART_TYPES = ["cartesian", "pie", "donut", "treemap"] as const;
const CHART_X_SCALES = ["category", "linear", "log", "time"] as const;
const AXES = ["primary", "secondary"] as const;

const opts = <T>(attrs: Attrs): T => attrs as T;

/** Text attributes shared by everything that draws a caption. */
const CAPTION = { text: "string", font: "font" } as const;

export function registerInputs(): void {
  registerElement<Label>({
    tag: "Label",
    attributes: {
      ...CAPTION,
      color: "color",
      textAlign: TEXT_ALIGNS,
      verticalAlign: ["top", "middle", "bottom"],
      autoSize: "boolean",
      ellipsis: "boolean",
      wrap: "boolean",
      lineHeight: "number",
    },
    create: (attrs) => new Label(opts(attrs)),
  });

  registerElement<LinkLabel>({
    tag: "LinkLabel",
    attributes: {
      ...CAPTION,
      color: "color",
      textAlign: TEXT_ALIGNS,
      wrap: "boolean",
      ellipsis: "boolean",
      alwaysUnderline: "boolean",
      linkColor: "color",
      href: "string",
    },
    create: (attrs) => new LinkLabel(opts(attrs)),
  });

  registerElement<Button>({
    tag: "Button",
    attributes: {
      ...CAPTION,
      isDefault: "boolean",
      textAlign: TEXT_ALIGNS,
      look: "string",
      lookColor: "colorRef",
      lit: "boolean",
    },
    create: (attrs) => new Button(opts(attrs)),
  });

  registerElement<CheckBox>({
    tag: "CheckBox",
    attributes: {
      ...CAPTION,
      checked: "boolean",
      boxOnRight: "boolean",
      threeState: "boolean",
      indeterminate: "boolean",
    },
    create: (attrs) => new CheckBox(opts(attrs)),
  });

  registerElement<RadioButton>({
    tag: "RadioButton",
    attributes: { ...CAPTION, checked: "boolean", boxOnRight: "boolean", group: "string" },
    create: (attrs) => new RadioButton(opts(attrs)),
  });

  registerElement<TextBox>({
    tag: "TextBox",
    attributes: {
      ...CAPTION,
      placeholder: "string",
      readOnly: "boolean",
      maxLength: "number",
      passwordChar: "string",
      frame: "boolean",
    },
    create: (attrs) => new TextBox(opts(attrs)),
  });

  registerElement<TextArea>({
    tag: "TextArea",
    base: "container",
    attributes: {
      ...CAPTION,
      placeholder: "string",
      readOnly: "boolean",
      maxLength: "number",
      wrap: "boolean",
      acceptsTab: "boolean",
      lineNumbers: "boolean",
      highlightCurrentLine: "boolean",
      undoLimit: "number",
    },
    create: (attrs) => new TextArea(opts(attrs)),
    // CDATA keeps its line breaks, which is the only sane way to write a block of text in an
    // attribute-shaped language.
    text: (area, text) => {
      area.text = text;
    },
  });

  registerElement<NumericUpDown>({
    tag: "NumericUpDown",
    base: "container",
    attributes: {
      value: "number",
      minimum: "number",
      maximum: "number",
      increment: "number",
      decimals: "number",
      suffix: "string",
      wrap: "boolean",
      readOnly: "boolean",
      font: "font",
    },
    create: (attrs) => new NumericUpDown(opts(attrs)),
  });

  registerElement<DatePicker>({
    tag: "DatePicker",
    base: "container",
    attributes: {
      value: "date",
      minimum: "date",
      maximum: "date",
      firstDayOfWeek: "number",
    },
    create: (attrs) => new DatePicker(opts(attrs)),
  });

  registerElement<Slider>({
    tag: "Slider",
    attributes: {
      orientation: ORIENTATIONS,
      minimum: "number",
      maximum: "number",
      value: "number",
      step: "number",
      smallChange: "number",
      largeChange: "number",
      tickFrequency: "number",
    },
    create: (attrs) => new Slider(opts(attrs)),
  });

  registerElement<ProgressBar>({
    tag: "ProgressBar",
    attributes: {
      value: "number",
      minimum: "number",
      maximum: "number",
      indeterminate: "boolean",
      showText: "boolean",
      font: "font",
      look: "string",
      lookColor: "colorRef",
    },
    create: (attrs) => new ProgressBar(opts(attrs)),
  });

  registerElement<Chart>({
    tag: "Chart",
    attributes: {
      title: "string",
      chartType: CHART_TYPES,
      orientation: ORIENTATIONS,
      kind: CHART_KINDS,
      stacked: "boolean",
      valueScale: ["linear", "log"],
      secondaryScale: ["linear", "log"],
      secondaryMinimum: "number",
      secondaryMaximum: "number",
      showXGrid: "boolean",
      upColor: "color",
      downColor: "color",
      hollowCandles: "boolean",
      candleColoring: ["open", "previousClose"],
      lastValueMarkers: "boolean",
      showXAxis: "boolean",
      skipGaps: "boolean",
      crosshair: "boolean",
      showTooltip: "boolean",
      valueAxisWidth: "number",
      secondaryAxisWidth: "number",
      link: "string",
      zoomable: "boolean",
      maxPoints: "number",
      xScale: CHART_X_SCALES,
      xValues: "string",
      holeRatio: "number",
      categories: "list",
      showLegend: "boolean",
      showGrid: "boolean",
      showMarkers: "boolean",
      lineWidth: "number",
      minimum: "number",
      maximum: "number",
      tickCount: "number",
      font: "font",
    },
    create: (attrs, ctx, element) => {
      const { xValues, link, ...rest } = attrs;
      const chart = new Chart(opts(rest));
      if (typeof xValues === "string") chart.xValues = parseXValues(xValues, chart.xScale, element, ctx);
      if (typeof link === "string" && link) linkNamed(ctx, link).add(chart);
      return chart;
    },
    childSpecs: {
      Series(element, ctx, chart) {
        const attrs = ctx.attributesOf(element, {
          name: "string",
          values: "string",
          ohlc: "string",
          lower: "string",
          curve: ["linear", "step"],
          baseValue: "number",
          totals: "list",
          boxSize: "number",
          x: "string",
          kind: CHART_KINDS,
          axis: ["primary", "secondary"],
          color: "color",
        });
        const prices = attrs["ohlc"] as string | undefined;
        const raw = (attrs["values"] as string | undefined) ?? (prices === undefined ? element.text : undefined);
        const series: ChartSeries = {
          name: (attrs["name"] as string) ?? `Series ${chart.series.length + 1}`,
          ...(raw === undefined ? {} : { values: parseSeriesValues(raw, element, ctx) }),
          ...(prices === undefined ? {} : { ohlc: parseOhlc(prices, element, ctx) }),
          ...(attrs["lower"] === undefined ? {} : { lower: parseSeriesValues(attrs["lower"] as string, element, ctx) }),
          ...(attrs["curve"] === undefined ? {} : { curve: attrs["curve"] as "linear" | "step" }),
          ...(attrs["baseValue"] === undefined ? {} : { baseValue: attrs["baseValue"] as number }),
          ...(attrs["boxSize"] === undefined ? {} : { boxSize: attrs["boxSize"] as number }),
          ...(attrs["totals"] === undefined ? {} : { totals: (attrs["totals"] as string[]).map(Number).filter(Number.isInteger) }),
          ...(attrs["x"] === undefined ? {} : { x: parseXValues(attrs["x"] as string, chart.xScale, element, ctx) }),
          ...(attrs["kind"] === undefined ? {} : { kind: attrs["kind"] as ChartSeries["kind"] }),
          ...(attrs["axis"] === undefined ? {} : { axis: attrs["axis"] as ChartSeries["axis"] }),
          ...(attrs["color"] === undefined ? {} : { color: attrs["color"] as ChartSeries["color"] }),
        };
        chart.series = [...chart.series, series];
      },
      Line(element, ctx, chart) {
        const a = ctx.attributesOf(element, { value: "number", label: "string", dashed: "boolean", color: "color", axis: AXES });
        if (a["value"] === undefined) return;
        chart.addAnnotation({ type: "line", value: a["value"] as number, ...annotationCommon(a) });
      },
      Trend(element, ctx, chart) {
        const a = ctx.attributesOf(element, { from: "string", to: "string", extend: "boolean", label: "string", color: "color", axis: AXES });
        const from = parsePoint(a["from"], element, ctx);
        const to = parsePoint(a["to"], element, ctx);
        if (!from || !to) return;
        chart.addAnnotation({ type: "trend", from, to, ...(a["extend"] === undefined ? {} : { extend: a["extend"] as boolean }), ...annotationCommon(a) });
      },
      XRange(element, ctx, chart) {
        const a = ctx.attributesOf(element, { from: "string", to: "string", label: "string", color: "color" });
        const from = parseAt(a["from"]);
        const to = parseAt(a["to"]);
        if (from === null || to === null) return;
        chart.addAnnotation({ type: "xrange", from, to, ...annotationCommon(a) });
      },
      YRange(element, ctx, chart) {
        const a = ctx.attributesOf(element, { from: "number", to: "number", label: "string", color: "color", axis: AXES });
        if (a["from"] === undefined || a["to"] === undefined) return;
        chart.addAnnotation({ type: "yrange", from: a["from"] as number, to: a["to"] as number, ...annotationCommon(a) });
      },
      Marker(element, ctx, chart) {
        const a = ctx.attributesOf(element, { at: "string", value: "number", shape: ["up", "down", "dot", "flag"], label: "string", color: "color", axis: AXES });
        const at = parseAt(a["at"]);
        if (at === null) return;
        chart.addAnnotation({
          type: "marker",
          at,
          ...(a["value"] === undefined ? {} : { value: a["value"] as number }),
          ...(a["shape"] === undefined ? {} : { shape: a["shape"] as "up" }),
          ...annotationCommon(a),
        });
      },
      VolumeProfile(element, ctx, chart) {
        const a = ctx.attributesOf(element, { prices: "string", volume: "string", bins: "number", width: "number", side: ["left", "right"], color: "color" });
        if (typeof a["prices"] !== "string" || typeof a["volume"] !== "string") return;
        chart.volumeProfile = {
          prices: a["prices"],
          volume: a["volume"],
          ...(a["bins"] === undefined ? {} : { bins: a["bins"] as number }),
          ...(a["width"] === undefined ? {} : { width: a["width"] as number }),
          ...(a["side"] === undefined ? {} : { side: a["side"] as "left" }),
          ...(a["color"] === undefined ? {} : { color: a["color"] as ChartSeries["color"] }),
        };
      },
    },
  });

  registerElement<ChartPanes>({
    tag: "ChartPanes",
    attributes: { gap: "number" },
    create: (attrs) => new ChartPanes(opts(attrs)),
    childSpecs: {
      Pane(element, ctx, panes) {
        const a = ctx.attributesOf(element, { weight: "number", minHeight: "number" });
        const inner = element.children.find((c) => c.name === "Chart");
        if (!inner) return;
        const chart = ctx.build(inner, null);
        if (!(chart instanceof Chart)) return;
        panes.addPane(chart, {
          ...(a["weight"] === undefined ? {} : { weight: a["weight"] as number }),
          ...(a["minHeight"] === undefined ? {} : { minHeight: a["minHeight"] as number }),
        });
      },
    },
  });

  registerElement<Separator>({
    tag: "Separator",
    attributes: {
      orientation: ORIENTATIONS,
      color: "color",
      thickness: "number",
      inset: "number",
    },
    create: (attrs) => new Separator(opts(attrs)),
  });

  registerElement<Spinner>({
    tag: "Spinner",
    attributes: { size: "number", spinSeconds: "number", spinning: "boolean" },
    create: (attrs) => new Spinner(opts(attrs)),
  });

  registerElement<ColorPicker>({
    tag: "ColorPicker",
    base: "container",
    attributes: {
      color: "color",
      mode: ["simple", "advanced"],
      columns: "number",
      showHex: "boolean",
      allowAlpha: "boolean",
    },
    create: (attrs) => new ColorPicker(opts(attrs)),
  });

  registerElement<ImageControl>({
    tag: "Image",
    aliases: ["ImageControl"],
    attributes: {
      source: "string",
      src: "string",
      sizeMode: ["none", "stretch", "fit", "fill", "center", "tile"],
      tint: "color",
      showBorder: "boolean",
      backgroundColor: "color",
    },
    create: (attrs) =>
      new ImageControl({
        ...opts<ConstructorParameters<typeof ImageControl>[0]>(attrs),
        ...(attrs["src"] ? { source: attrs["src"] as string } : {}),
      }),
  });

  registerElement<ListBox>({
    tag: "ListBox",
    base: "container",
    attributes: {
      items: "list",
      selectionMode: SELECTION_MODES,
      selectedIndex: "number",
      itemHeight: "number",
      font: "font",
    },
    create: (attrs) => new ListBox(opts(attrs)),
    childSpecs: { Item: collectItems },
  });

  registerElement<ComboBox>({
    tag: "ComboBox",
    base: "container",
    attributes: {
      items: "list",
      selectedIndex: "number",
      editable: "boolean",
      placeholder: "string",
      maxDropDownItems: "number",
      font: "font",
    },
    create: (attrs) => new ComboBox(opts(attrs)),
    childSpecs: { Item: collectItems },
  });

  registerElement<ListView>({
    tag: "ListView",
    base: "container",
    attributes: {
      items: "list",
      selectionMode: SELECTION_MODES,
      rowHeight: "number",
      showGridLines: "boolean",
      font: "font",
    },
    create: (attrs) => new ListView(opts(attrs)),
    childSpecs: {
      Column(element, ctx, list) {
        const attrs = ctx.attributesOf(element, {
          text: "string",
          width: "number",
          minWidth: "number",
          align: TEXT_ALIGNS,
          name: "string",
        });
        const column: ListViewColumn = {
          text: (attrs["text"] as string) ?? element.text,
          width: (attrs["width"] as number) ?? 100,
          ...(attrs["minWidth"] === undefined ? {} : { minWidth: attrs["minWidth"] as number }),
          ...(attrs["align"] === undefined ? {} : { align: attrs["align"] as "left" }),
        };
        list.columns = [...list.columns, column];
        nameIt(attrs, ctx, column);
      },
    },
  });

  registerElement<TreeView>({
    tag: "TreeView",
    base: "container",
    attributes: {
      selectionMode: SELECTION_MODES,
      indent: "number",
      showLines: "boolean",
      showRootLines: "boolean",
      checkBoxes: "boolean",
      itemHeight: "number",
      font: "font",
    },
    create: (attrs) => new TreeView(opts(attrs)),
    childSpecs: {
      Node(element, ctx, tree) {
        tree.nodes = [...tree.nodes, readNode(element, ctx)];
      },
    },
  });
}

/** `<Item>Work</Item>` as an alternative to `items="Work, Home"`. */
function collectItems(
  element: XmlElement,
  ctx: BuildContext,
  list: { items: readonly unknown[] } & { items: unknown },
): void {
  const attrs = ctx.attributesOf(element, { text: "string", name: "string" });
  const text = (attrs["text"] as string) ?? element.text;
  (list as { items: unknown[] }).items = [...(list.items as unknown[]), text];
  nameIt(attrs, ctx, text);
}

/** A `<Node>` and everything under it. */
function readNode(element: XmlElement, ctx: BuildContext): TreeNodeSpec {
  const attrs = ctx.attributesOf(element, {
    text: "string",
    expanded: "boolean",
    checked: "boolean",
    enabled: "boolean",
    name: "string",
  });
  const node: TreeNodeSpec = {
    text: (attrs["text"] as string) ?? element.text,
    ...(attrs["expanded"] === undefined ? {} : { expanded: attrs["expanded"] as boolean }),
    ...(attrs["checked"] === undefined ? {} : { checked: attrs["checked"] as boolean }),
    ...(attrs["enabled"] === undefined ? {} : { enabled: attrs["enabled"] as boolean }),
  };
  const children = element.children.filter((c) => c.name === "Node");
  if (children.length > 0) node.children = children.map((c) => readNode(c, ctx));
  nameIt(attrs, ctx, node);
  return node;
}

/** Register a named piece of data so `view.spec(name)` can reach it later. */
function nameIt(attrs: Attrs, ctx: BuildContext, spec: unknown): void {
  const name = attrs["name"];
  if (typeof name === "string" && name) ctx.nameSpec(name, spec);
}

/**
 * `values="12, 18, , 9"` - a series' numbers. An empty entry, or `-`, is a gap; anything else
 * that is not a number is a gap too, with a warning, so one typo costs one point and not the chart.
 */
function parseSeriesValues(text: string, at: XmlElement, ctx: BuildContext): (number | null)[] {
  if (!text.trim()) return [];
  return text.split(",").map((part) => {
    const s = part.trim();
    if (s === "" || s === "-") return null;
    const v = Number(s);
    if (Number.isFinite(v)) return v;
    ctx.warn({ message: `a series value expected a number, not "${s}"`, line: at.line, col: at.col });
    return null;
  });
}

/**
 * `x="1, 2.5, 4"` - a chart's x values, or on a time axis `x="2026-03-01, 2026-03-08"`, anything
 * `Date` reads. An empty entry is a gap; anything unreadable is a gap too, with a warning.
 */
function parseXValues(text: string, scale: string, at: XmlElement, ctx: BuildContext): (number | null)[] {
  if (!text.trim()) return [];
  return text.split(",").map((part) => {
    const s = part.trim();
    if (s === "" || s === "-") return null;
    const v = scale === "time" && !/^-?\d+(\.\d+)?$/.test(s) ? Date.parse(s) : Number(s);
    if (Number.isFinite(v)) return v;
    ctx.warn({ message: `an x value expected ${scale === "time" ? "a date" : "a number"}, not "${s}"`, line: at.line, col: at.col });
    return null;
  });
}

/**
 * `ohlc="10 12 9 11; 11 13 10 12"` - a financial series' prices, one period per `;`, each its
 * open, high, low and close. An empty period, or `-`, is a gap; a period that is not four numbers
 * is a gap too, with a warning.
 */
function parseOhlc(text: string, at: XmlElement, ctx: BuildContext): (Ohlc | null)[] {
  if (!text.trim()) return [];
  return text.split(";").map((part) => {
    const s = part.trim();
    if (s === "" || s === "-") return null;
    const n = s.split(/[\s,/]+/).map(Number);
    if (n.length === 4 && n.every(Number.isFinite)) return { open: n[0]!, high: n[1]!, low: n[2]!, close: n[3]! };
    ctx.warn({ message: `a period expected open, high, low and close, not "${s}"`, line: at.line, col: at.col });
    return null;
  });
}

/** The links of each loaded document, by the name its charts' `link` attributes give. */
const documentLinks = new WeakMap<object, Map<string, ChartLink>>();

/** `link="prices"`: every chart in the document naming the same link moves with the others. */
function linkNamed(ctx: BuildContext, name: string): ChartLink {
  let byName = documentLinks.get(ctx.view);
  if (!byName) documentLinks.set(ctx.view, (byName = new Map()));
  let link = byName.get(name);
  if (!link) byName.set(name, (link = new ChartLink()));
  return link;
}

/** A number, or a date `Date` can read: where an annotation sits along the category axis. */
function parseAt(text: unknown): ChartAt | null {
  if (typeof text !== "string" || !text.trim()) return null;
  const s = text.trim();
  if (/^-?\d+(\.\d+)?$/.test(s)) return Number(s);
  const t = Date.parse(s);
  return Number.isFinite(t) ? new Date(t) : null;
}

/** `from="2026-03-02, 180"`: a trend line's end, where along the axis and at what value. */
function parsePoint(text: unknown, at: XmlElement, ctx: BuildContext): { at: ChartAt; value: number } | null {
  if (typeof text !== "string") return null;
  const comma = text.lastIndexOf(",");
  const where = comma < 0 ? null : parseAt(text.slice(0, comma));
  const value = comma < 0 ? Number.NaN : Number(text.slice(comma + 1).trim());
  if (where !== null && Number.isFinite(value)) return { at: where, value };
  ctx.warn({ message: `a trend end expected "where, value", not "${text}"`, line: at.line, col: at.col });
  return null;
}

/** The label, color and axis every annotation may carry. */
function annotationCommon(a: Attrs): { label?: string; color?: ChartSeries["color"]; axis?: "primary" | "secondary"; dashed?: boolean } {
  return {
    ...(a["label"] === undefined ? {} : { label: a["label"] as string }),
    ...(a["color"] === undefined ? {} : { color: a["color"] as ChartSeries["color"] }),
    ...(a["axis"] === undefined ? {} : { axis: a["axis"] as "primary" | "secondary" }),
    ...(a["dashed"] === undefined ? {} : { dashed: a["dashed"] as boolean }),
  };
}
